import React, { useState, useEffect } from 'react';
import { Route, Redirect } from 'react-router-dom';
import AuthValidationService from './services/AuthValidationService';

export const EnhancedGuard = ({ component: Component, token: Token, routeRedirect, componentProps, ...rest }) => {
    const [authState, setAuthState] = useState({
        isChecking: true,
        isAuthenticated: false,
        shouldRedirect: false
    });

    useEffect(() => {
        const validateAuth = async () => {
            try {
                setAuthState(prev => ({ ...prev, isChecking: true }));

                // Si no hay token, redirigir
                if (!localStorage.getItem(Token)) {
                    setAuthState({
                        isChecking: false,
                        isAuthenticated: false,
                        shouldRedirect: true
                    });
                    return;
                }

                // Para tokens de admin, verificar existencia de ambos tokens
                if (Token === 'admin-token') {
                    const hasUserToken = !!localStorage.getItem('user-token');
                    const hasAdminToken = !!localStorage.getItem('admin-token');
                    
                    if (hasUserToken && hasAdminToken) {
                        setAuthState({
                            isChecking: false,
                            isAuthenticated: true,
                            shouldRedirect: false
                        });
                    } else {
                        setAuthState({
                            isChecking: false,
                            isAuthenticated: false,
                            shouldRedirect: true
                        });
                    }
                    return;
                }

                // Para user-token, hacer validación completa
                if (Token === 'user-token') {
                    // Primero validación local rápida
                    const localValidation = AuthValidationService.validateTokenLocally();
                    
                    if (!localValidation.isValid) {
                        setAuthState({
                            isChecking: false,
                            isAuthenticated: false,
                            shouldRedirect: true
                        });
                        return;
                    }

                    // Si el token es válido localmente, intentar validación con servidor
                    try {
                        const serverValidation = await AuthValidationService.validateSession();
                        
                        if (serverValidation.isValid) {
                            setAuthState({
                                isChecking: false,
                                isAuthenticated: true,
                                shouldRedirect: false
                            });
                        } else if (serverValidation.reason === 'network-error') {
                            // Si hay error de red pero el token es válido localmente, permitir acceso
                            setAuthState({
                                isChecking: false,
                                isAuthenticated: true,
                                shouldRedirect: false
                            });
                        } else {
                            // Token no válido según el servidor
                            setAuthState({
                                isChecking: false,
                                isAuthenticated: false,
                                shouldRedirect: true
                            });
                        }
                    } catch (error) {
                        // Error inesperado, pero si el token es válido localmente, permitir acceso
                        setAuthState({
                            isChecking: false,
                            isAuthenticated: true,
                            shouldRedirect: false
                        });
                    }
                }
            } catch (error) {
                console.error('Error en validación de Guard:', error);
                setAuthState({
                    isChecking: false,
                    isAuthenticated: false,
                    shouldRedirect: true
                });
            }
        };

        validateAuth();
    }, [Token]);

    return (
        <Route {...rest} render={props => {
            if (authState.isChecking) {
                return (
                    <div className="min-h-screen flex items-center justify-center bg-gray-50">
                        <div className="text-center">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                            <p className="text-sm text-gray-600">Validando acceso...</p>
                        </div>
                    </div>
                );
            }

            if (authState.shouldRedirect) {
                return <Redirect to={{ pathname: routeRedirect, state: { from: props.location } }} />;
            }

            if (authState.isAuthenticated) {
                return <Component {...props} {...componentProps} />;
            }

            // Fallback - mostrar error
            return (
                <div className="min-h-screen flex items-center justify-center bg-gray-50">
                    <div className="text-center">
                        <p className="text-red-600 mb-4">Error de autenticación</p>
                        <button
                            onClick={() => window.location.href = routeRedirect}
                            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                        >
                            Ir al Login
                        </button>
                    </div>
                </div>
            );
        }} />
    );
};

// Mantener el Guard original para compatibilidad
export const Guard = ({ component: Component, token: Token, routeRedirect, componentProps, ...rest }) => (
    <Route {...rest} render={props => (
        localStorage.getItem(Token) 
            ? <Component {...props} {...componentProps} />
            : <Redirect to={{ pathname: routeRedirect, state: { from: props.location } }} />
    )} />
);